import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';

import AccessibilityMenu from 'Layout/NavBar/MegaMenu/AccessibilityMenu';
import ProductsMegaMenuTabLink from 'Layout/NavBar/MegaMenu/ProductsMegaMenuTabLink';
import ProductsMegaMenuTab from 'Layout/NavBar/MegaMenu/ProductsMegaMenuTab';

const messages = defineMessages({
  gamebundles: {
    id: 'nav.bundle-menu.gamebundles',
    defaultMessage: 'Game Bundles',
  },
  gamebundlestext: {
    id: 'nav.bundle-menu.gamebundlestext',
    defaultMessage: 'Fanatical’s game bundles bring together the latest and greatest PC Steam games at prices you won’t find anywhere else. Our bundles are exclusive, limited-time collections of games at great prices.',
  },
  bookbundles: {
    id: 'nav.bundle-menu.bookbundles',
    defaultMessage: 'Book Bundles',
  },
  bookbundlestext: {
    id: 'nav.bundle-menu.bookbundlestext',
    defaultMessage: 'Whether you’re looking to learn a new skill, expand your knowledge on topics you’re passionate about or looking to expand your comic book collection, we have the bundles for you.',
  },
  softwarebundles: {
    id: 'nav.bundle-menu.softwarebundles',
    defaultMessage: 'Software Bundles',
  },
  softwarebundlestext: {
    id: 'nav.bundle-menu.softwarebundlestext',
    defaultMessage: 'Whether you’re looking for new assets for game creation, audio files for your music projects or utilities to help your PC run more smoothly - we’ve got you covered.',
  },
  allbundles: {
    id: 'nav.bundle-menu.allbundles',
    defaultMessage: 'All Bundles',
  },
  allbundlestext: {
    id: 'nav.bundle-menu.allbundlestext',
    defaultMessage: 'Fanatical’s bundles offer the deals for you! Featuring the biggest and best Steam bundles for PC, Mac and Linux, as well as VR, eBooks, Software and more, you’ll typically find savings of 95% or more.',
  },
});

function BundlesMegaMenu({
  intl,
  megaMenuTab,
  setMegaMenuTab,
  bundleHits,
  gameBundleHits,
  bookBundleHits,
  softwareBundleHits,
}) {
  const [tab, setTab] = useState('gamebundles');

  const { formatMessage } = intl;

  const toggleTab = (newTab) => {
    if (tab !== newTab) {
      setTab(newTab);
    }
  };

  // To avoid loading the large megamenu into the DOM, create a simple link structure for each tab below.
  const menuLinks = [
    { name: formatMessage(messages.gamebundles), link: '/bundle/games', products: gameBundleHits, tabId: 'gamebundles' },
    { name: formatMessage(messages.bookbundles), link: '/bundle/books', products: bookBundleHits, tabId: 'bookbundles' },
    { name: formatMessage(messages.softwarebundles), link: '/bundle/software', products: softwareBundleHits, tabId: 'softwarebundles' },
    { name: formatMessage(messages.allbundles), link: '/bundle', products: bundleHits, tabId: 'allbundles' },
  ];

  return (
    <div
      id="MegaMenu_bundles"
      className="container MegaMenuContainer MegaMenuContainer__bundles"
      onMouseEnter={() => setMegaMenuTab('bundles')}
      onMouseLeave={() => setMegaMenuTab('')}
    >
      <AccessibilityMenu menuLinks={menuLinks} from="bundles" />
      {megaMenuTab === 'bundles' &&
        <div className={classNames('MegaMenu', { 'MegaMenu--visible': megaMenuTab === 'bundles' })}>
          <div className="ProductsMegaMenu">
            <div className="ProductsMegaMenu__tabLinks">
              <ul className="ProductsMegaMenu__linksContainer" role="menu" id="MegaMenu_bundles">
                {menuLinks.map(menuLink =>
                  <ProductsMegaMenuTabLink key={menuLink.tabId} name={menuLink.name} link={menuLink.link} tab={tab} toggle={toggleTab} tabId={menuLink.tabId} from="bundles" setMegaMenuTab={setMegaMenuTab} />,
                )}
              </ul>
            </div>

            <ProductsMegaMenuTab
              tab={tab}
              title={formatMessage(messages.gamebundles)}
              hits={gameBundleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'gamebundles'}
              linkTo={'/bundle/games'}
              subText={formatMessage(messages.gamebundlestext)}
            />

            <ProductsMegaMenuTab
              tab={tab}
              title={formatMessage(messages.bookbundles)}
              hits={bookBundleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'bookbundles'}
              linkTo={'/bundle/books'}
              subText={formatMessage(messages.bookbundlestext)}
            />

            <ProductsMegaMenuTab
              tab={tab}
              title={formatMessage(messages.softwarebundles)}
              hits={softwareBundleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'softwarebundles'}
              linkTo={'/bundle/software'}
              subText={formatMessage(messages.softwarebundlestext)}
            />

            <ProductsMegaMenuTab
              tab={tab}
              title={formatMessage(messages.allbundles)}
              hits={bundleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'allbundles'}
              linkTo={'/bundle'}
              subText={formatMessage(messages.allbundlestext)}
            />
          </div>
        </div>
      }
    </div>
  );
}

BundlesMegaMenu.propTypes = {
  megaMenuTab: PropTypes.string,
  setMegaMenuTab: PropTypes.func,
  bundleHits: PropTypes.arrayOf(PropTypes.object),
  gameBundleHits: PropTypes.arrayOf(PropTypes.object),
  bookBundleHits: PropTypes.arrayOf(PropTypes.object),
  softwareBundleHits: PropTypes.arrayOf(PropTypes.object),
};

BundlesMegaMenu.defaultProps = {
  megaMenuTab: '',
  setMegaMenuTab: () => {},
  bundleHits: [],
  gameBundleHits: [],
  bookBundleHits: [],
  softwareBundleHits: [],
};

export default injectIntl(BundlesMegaMenu);
